import { useEffect, useState, type FormEvent } from 'react';
import {
  describeError,
  setFeatureFlagTenantOverride,
  getTenantSummary,
  type FeatureFlagTenantOverrideDto,
  type TenantSummary,
} from '@/lib/api';
import { TenantSelector } from '@/components/TenantSelector';

interface Props {
  flagKey: string;
  existing: FeatureFlagTenantOverrideDto | null;
  onClose: () => void;
  onSaved: () => void;
}

export function FeatureFlagTenantOverrideModal({ flagKey, existing, onClose, onSaved }: Props) {
  const [tenant, setTenant] = useState<TenantSummary | null>(null);
  const [enabled, setEnabled] = useState(existing?.enabled ?? false);
  const [rollout, setRollout] = useState<string>(existing?.rolloutPercentage?.toString() ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editing an existing override: the tenant is fixed. Resolve its name/slug
  // for a readable display instead of a bare GUID.
  useEffect(() => {
    if (!existing) return;
    let cancelled = false;
    void getTenantSummary(existing.tenantId).then((t) => {
      if (cancelled) return;
      setTenant(t ?? {
        id: existing.tenantId, tradeName: '', ownerName: '',
        slug: '', phone: '', isActive: false, subscriptionStatus: '',
      });
    });
    return () => { cancelled = true; };
  }, [existing]);

  const rolloutOk = rollout === '' || /^([0-9]|[1-9][0-9]|100)$/.test(rollout);
  const canSubmit = tenant !== null && rolloutOk && !submitting;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit || !tenant) return;
    setError(null);
    setSubmitting(true);
    try {
      await setFeatureFlagTenantOverride(flagKey, tenant.id, {
        enabled,
        rolloutPercentage: rollout === '' ? null : Number(rollout),
      });
      onSaved();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div role="presentation"
      style={{ position: 'fixed', inset: 0, background: 'var(--color-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 40 }}>
      <div role="dialog" aria-modal="true" aria-labelledby="ff-override-title"
        style={{ background: 'var(--color-surface)', borderRadius: 12, padding: 24, maxWidth: 480, width: '90%', boxShadow: 'var(--shadow-lg)' }}>
        <h2 id="ff-override-title" style={{ marginTop: 0, fontSize: 18 }}>
          {existing ? 'Editar exceção' : 'Adicionar exceção'}
        </h2>
        <form onSubmit={onSubmit}>
          {error && <div className="error-banner">{error}</div>}

          <div className="field">
            <label htmlFor="ff-tenant">Loja</label>
            <TenantSelector
              id="ff-tenant"
              value={tenant}
              onChange={setTenant}
              disabled={existing !== null}
            />
          </div>

          <div className="field">
            <label>
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Ligada
            </label>
          </div>

          <div className="field">
            <label htmlFor="ff-tenant-rollout">Percentual (opcional)</label>
            <input id="ff-tenant-rollout" type="text" value={rollout} onChange={(e) => setRollout(e.target.value)} placeholder="vazio = sem percentual" />
            {!rolloutOk && (
              <div style={{ fontSize: 12, color: 'var(--color-error)' }}>Valor inválido</div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancelar</button>
            <button type="submit" className="btn" disabled={!canSubmit}
              style={{ padding: '8px 16px', borderRadius: 8 }}>
              {submitting ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
